import { RiExternalLinkLine } from "@remixicon/react";
import type { DensitySnapshot } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { tickerLabel } from "@/lib/labels";
import {
  bindingLabel,
  NEEDS_GROUPS_REASON_TEXT,
  type TerminalAction,
  terminalActionFor,
  terminalName,
  unavailableReasonText,
} from "./terminal-action";
import { type TerminalControls, useTerminalControls } from "./terminal-controls";
import { useNextBinding, useNextGroup } from "./terminal-group-rotation";
import type { TerminalKind } from "./terminal-settings";

/**
 * Действие «открыть в терминале» (§10 1.45): кнопка без меню на строке
 * таблицы и в детальной панели. Клик отправляет инструмент в следующую
 * группу круга; при пустом наборе групп ведёт в настройки (design D2).
 *
 * Строка таблицы — `role="button"` с `onClick`/`onKeyDown`, поэтому клик на
 * кнопке и `Enter`/`Space` на ней гасятся здесь: действие над инструментом не
 * должно ни открывать панель, ни двигать выделение строки. Остальные
 * клавиши, в первую очередь стрелки, обязаны дойти до строки (роверная
 * навигация таблицы), как у `stopButtonActivation` в `density-row.tsx`.
 */

function stopRowEvent(event: ReactMouseEvent | ReactKeyboardEvent): void {
  event.stopPropagation();
}

/** Гасит только активацию: `Enter`/`Space`. `preventDefault` не нужен — нативный клик и есть отправка. */
function stopActivationKey(event: ReactKeyboardEvent): void {
  if (event.key === "Enter" || event.key === " ") {
    event.stopPropagation();
  }
}

function baseLabel(row: DensitySnapshot, terminal: TerminalKind): string {
  return `Открыть ${tickerLabel(row.instrument, row.exchange_base)} в ${terminalName(terminal)}`;
}

/** Что делает действие: отправляет в круг или, при пустом наборе, ведёт в настройки. */
function activate(
  controls: TerminalControls,
  action: Extract<TerminalAction, { command: unknown }>,
  densityId: string,
): void {
  if (action.kind === "needs_groups") {
    controls.onOpenSettings("groups");
    return;
  }
  controls.onSend(action.command, densityId);
}

function TerminalButton({
  row,
  action,
  controls,
  tabIndex,
}: {
  row: DensitySnapshot;
  action: Extract<TerminalAction, { command: unknown }>;
  controls: TerminalControls;
  /** В строке таблицы кнопка вне роверного `tabindex` (`-1`), в панели — обычная. */
  tabIndex?: number;
}) {
  const { terminal } = controls.settings;
  const nextGroup = useNextGroup(controls.settings.groups);
  const nextBinding = useNextBinding(controls.settings.metascalp.bindings.map(bindingLabel));
  const needsGroups = action.kind === "needs_groups";
  // Подпись называет группу (связку), в которую уйдёт следующая отправка;
  // состояние связи — второй строкой подсказки (раньше его показывало меню).
  // Пустой набор связок MetaScalp — подпись без связки: уйдёт в активное окно.
  const label = needsGroups
    ? "Выберите группы окон TigerTrade"
    : terminal === "metascalp"
      ? nextBinding === null
        ? baseLabel(row, terminal)
        : `${baseLabel(row, terminal)} — связка ${nextBinding}`
      : `${baseLabel(row, terminal)} — группа ${nextGroup ?? ""}`;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            tabIndex={tabIndex}
            aria-label={label}
            data-link-state={controls.linkState.state}
            className="size-6 text-muted-foreground"
            onClick={(event) => {
              stopRowEvent(event);
              activate(controls, action, row.density_id);
            }}
            onKeyDown={stopActivationKey}
          />
        }
      >
        <RiExternalLinkLine className="size-3.5" />
      </TooltipTrigger>
      {/* Подсказка в портале, а React-события всплывают и через портал к строке. */}
      <TooltipContent onClick={stopRowEvent} onKeyDown={stopRowEvent}>
        <div className="flex flex-col gap-0.5" data-slot="terminal-tooltip">
          <span>{label}</span>
          {needsGroups ? null : <span>{controls.statusText}</span>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Обработчик действия терминала для строки — хоткей `t` и кнопка делают одно
 * и то же. `undefined` — кнопки на строке нет: нет контекста терминала,
 * соответствия рынка или нативного имени. Пустой набор групп кнопку не
 * убирает: действие ведёт в настройки.
 */
export function useTerminalRowTrigger(row: DensitySnapshot): (() => void) | undefined {
  const controls = useTerminalControls();
  if (controls === null) {
    return undefined;
  }
  const action = terminalActionFor(row, controls.settings);
  if (action.kind === "unavailable") {
    return undefined;
  }
  return () => activate(controls, action, row.density_id);
}

/**
 * Пустое место действия (§64 п. 3, 1.51): у каждого значка колонки своё
 * постоянное место, и без терминала остальные не съезжают. Не кнопка и не
 * в табуляции: действия на строке нет, и предлагать его нельзя.
 */
function TerminalRowActionGap() {
  return <span aria-hidden="true" data-slot="terminal-row-action-gap" className="size-6" />;
}

/**
 * Строка таблицы: кнопка, когда действие есть, иначе пустое место той же
 * ширины (спека — «действия не предлагает», но колонка не прыгает).
 */
export function TerminalRowAction({ row }: { row: DensitySnapshot }) {
  const controls = useTerminalControls();
  if (controls === null) {
    return <TerminalRowActionGap />;
  }
  const action = terminalActionFor(row, controls.settings);
  if (action.kind === "unavailable") {
    return <TerminalRowActionGap />;
  }
  return <TerminalButton row={row} action={action} controls={controls} tabIndex={-1} />;
}

/**
 * Детальная панель: та же кнопка, а когда действия нет или не выбраны группы —
 * недоступная кнопка, причина словами и путь к настройкам ниже. Скрытое без
 * объяснения действие в панели читалось бы как неисправность.
 */
export function TerminalDetailAction({ row }: { row: DensitySnapshot }) {
  const controls = useTerminalControls();
  if (controls === null) {
    return null;
  }
  const action = terminalActionFor(row, controls.settings);
  if (action.kind === "available") {
    return <TerminalButton row={row} action={action} controls={controls} />;
  }
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      disabled
      aria-label={`${baseLabel(row, controls.settings.terminal)} — недоступно`}
      className="size-6 text-muted-foreground"
    >
      <RiExternalLinkLine className="size-3.5" />
    </Button>
  );
}

/**
 * Строка под заголовком панели: путь к настройкам терминала — всегда, а когда
 * действие недоступно, перед ним — причина словами.
 */
export function TerminalDetailSettings({ row }: { row: DensitySnapshot }) {
  const controls = useTerminalControls();
  if (controls === null) {
    return null;
  }
  const action = terminalActionFor(row, controls.settings);
  const reason =
    action.kind === "unavailable"
      ? unavailableReasonText(action.reason, row.instrument, controls.settings.terminal)
      : action.kind === "needs_groups"
        ? NEEDS_GROUPS_REASON_TEXT
        : null;
  return (
    <p className="text-xs text-muted-foreground">
      {reason === null ? null : `${reason}. `}
      <Button
        variant="link"
        size="xs"
        className="h-auto p-0 text-xs"
        onClick={() =>
          controls.onOpenSettings(action.kind === "needs_groups" ? "groups" : undefined)
        }
      >
        Настройки {terminalName(controls.settings.terminal)}…
      </Button>
    </p>
  );
}
